import MessageFormValidate from "components/shared-components/MessageFormValidate";
import React, { useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import { formatMultiSelect } from "helpers/formatMultiSelect";
import { getListTransportGroup } from "store/group-transport/actions";

export const useTransportGroupData = () => {
  const dispatch = useDispatch();
  const groupTransport = useSelector(state => state.groupTransport.list);

  const optionsSelect = useMemo(() => {
    if (
      !groupTransport?.isLoading &&
      groupTransport?.items &&
      groupTransport?.items.length > 0
    ) {
      return [
        {
          label: "Tất cả",
          value: "all",
        },
        ...groupTransport?.items.map(item => ({
          label: item.name,
          value: item.code,
        })),
      ];
    }

    return [];
  }, [groupTransport]);

  const getGroupTransportOption = (value, optionsSelect) => {
    if (value && value !== "") {
      return optionsSelect.find(item => item.value === value);
    }

    return undefined;
  };

  useEffect(() => {
    dispatch(
      getListTransportGroup({
        record_status: "O",
        page: -1,
      })
    );
  }, []);

  return {
    groupTransport,
    optionsSelect,
    getGroupTransportOption,
  };
};

export const GroupTransportFilter = ({
  onChange,
  defaultValue,
  runnerVisible = true,
  primary = true,
  label,
  watchValue = undefined,
  ...props
}) => {
  const { t } = useTranslation(["transport"]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(undefined);
  const { optionsSelect, getGroupTransportOption } = useTransportGroupData();

  const handleChange = selected => {
    if (selected.value === "all") {
      onChange(undefined);
      setSelectedValue(getGroupTransportOption("all", optionsSelect));
      return;
    }
    onChange(selected);
    setSelectedValue(selected);
  };

  const labelDisplay = useMemo(() => {
    if (selectedValue) {
      return <span>:&nbsp;{selectedValue.label}</span>;
    }

    return <></>;
  }, [selectedValue, watchValue]);

  useEffect(() => {
    if (watchValue) {
      setSelectedValue(getGroupTransportOption(watchValue, optionsSelect));
    }
  }, [watchValue]);

  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen(prev => !prev)}>
      <DropdownToggle
        className={`btn ${primary ? "primary-btn" : ""} w-100`}
        caret
      >
        {label ? label : t("group_transport")}
        {labelDisplay}
        <i className="mdi mdi-chevron-down" />
      </DropdownToggle>
      <DropdownMenu
        className="w-100"
        style={{ overflow: "auto", maxHeight: "224px" }}
      >
        {optionsSelect?.map((item, index) => {
          const isActive = selectedValue?.value === item.value;
          if (!runnerVisible && item.value === "runner") return;
          return (
            <DropdownItem
              style={{
                whiteSpace: "pre-wrap",
                background: isActive
                  ? primary
                    ? "#556ee6"
                    : "#74788d"
                  : "none",
                color: isActive && "#eff2f7",
              }}
              key={`group-transport-${index}`}
              onClick={() => handleChange(item)}
              active={selectedValue?.value === item.value}
            >
              {item?.label}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};

export const GroupTransportMultiFilter = ({
  onChange,
  label = "",
  selectedCodes = [],
  runnerVisible = true,
  primary = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { optionsSelect } = useTransportGroupData();

  const optionsWithoutAll = useMemo(
    () => optionsSelect.filter(item => item.value !== "all"),
    [optionsSelect]
  );

  const visibleOptions = useMemo(() => {
    if (runnerVisible) {
      return optionsWithoutAll;
    }

    return optionsWithoutAll.filter(item => item.value !== "runner");
  }, [optionsWithoutAll, runnerVisible]);

  const summaryLabel = useMemo(() => {
    const labels = selectedCodes.map(code => {
      const opt = optionsWithoutAll.find(item => item.value === code);

      return opt?.label || code;
    });

    return formatMultiSelect(labels);
  }, [selectedCodes, optionsWithoutAll]);

  const toggleCode = itemValue => {
    if (itemValue === "all") {
      onChange(undefined);

      return;
    }

    const isChecked = selectedCodes.includes(itemValue);
    const next = isChecked
      ? selectedCodes.filter(c => c !== itemValue)
      : [...selectedCodes, itemValue];
    onChange(next.length ? next : undefined);
  };

  return (
    <Dropdown
      className="d-inline-block align-middle mw-100"
      isOpen={isOpen}
      toggle={() => setIsOpen(prev => !prev)}
    >
      <DropdownToggle
        className={`btn ${primary ? "primary-btn text-overflow" : ""} d-inline-flex align-items-center`}
        caret
        style={{
          width: "fit-content",
          maxWidth: "min(420px, calc(100vw - 24px))",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        <span
          style={{
            display: "inline-block",
            maxWidth: "min(220px, 55vw)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "bottom",
          }}
        >
          :&nbsp;{summaryLabel}
        </span>
      </DropdownToggle>
      <DropdownMenu
        className="border shadow-sm"
        style={{
          width: "max-content",
          minWidth: "100%",
          maxWidth: "520px",
          maxHeight: 320,
          overflowX: "auto",
          overflowY: "auto",
          paddingTop: 6,
          paddingBottom: 6,
          scrollbarWidth: "thin",
          boxSizing: "border-box",
        }}
      >
        <DropdownItem
          toggle={false}
          className="px-3 py-2 text-dark font-weight-medium"
          style={{ cursor: "pointer", whiteSpace: "nowrap" }}
          onClick={() => toggleCode("all")}
        >
          Tất cả
        </DropdownItem>
        <DropdownItem divider />
        {visibleOptions.map((item, index) => {
          const isChecked = selectedCodes.includes(item.value);

          return (
            <DropdownItem
              toggle={false}
              key={`group-transport-multi-${item.value}-${index}`}
              className="d-flex align-items-center px-3 py-2 text-dark"
              style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              onClick={() => toggleCode(item.value)}
            >
              <Input
                type="checkbox"
                className="form-check-input position-static flex-shrink-0 m-0"
                style={{
                  width: "1.125rem",
                  height: "1.125rem",
                  pointerEvents: "none",
                }}
                checked={isChecked}
                readOnly
                tabIndex={-1}
              />
              <span className="ms-2">{item.label}</span>
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};

export const GroupTransportSelectInput = ({
  fieldName,
  mainForm,
  isLabel = true,
  required = false,
  ...extraProps
}) => {
  const { t } = useTranslation(["transport"]);
  const { optionsSelect } = useTransportGroupData();

  const {
    formState: { errors },
    control,
  } = mainForm;

  return (
    <FormGroup>
      {isLabel && (
        <Label htmlFor={fieldName}>
          {t("group_transport")}{" "}
          {required && <span className="text-danger">*</span>}
        </Label>
      )}
      <Controller
        control={control}
        name={fieldName}
        render={({ field }) => (
          <Select
            {...field}
            placeholder={t("placeholder.select", {
              title: t("group_transport"),
            })}
            name={fieldName}
            options={optionsSelect.filter(item => item.value !== "all")}
            classNamePrefix="select2-selection"
            {...extraProps}
          />
        )}
      />
      {errors[fieldName] && (
        <MessageFormValidate error={errors[fieldName].message} />
      )}
    </FormGroup>
  );
};
